import { io } from 'socket.io-client';
import resources from '../resources';
import config from '../services/config';
import { store } from '../store/store';

export type ServerRoomSubscriptionState = 'unknown' | 'subscribed' | 'denied';

type ServerActionPayload = {
  type?: unknown;
  data?: unknown;
};

const roomSubscriptionStateByRoom = new Map<
  string,
  ServerRoomSubscriptionState
>();
const roomSubscriptionListeners = new Set<
  (room: string, state: ServerRoomSubscriptionState) => void
>();

function setRoomSubscriptionState(
  room: string,
  state: ServerRoomSubscriptionState,
): void {
  roomSubscriptionStateByRoom.set(room, state);
  for (const listener of roomSubscriptionListeners) {
    listener(room, state);
  }
}

export function getSocketRoomSubscriptionState(
  room: string,
): ServerRoomSubscriptionState {
  return roomSubscriptionStateByRoom.get(room) ?? 'unknown';
}

export function addServerRoomSubscriptionListener(
  listener: (room: string, state: ServerRoomSubscriptionState) => void,
): () => void {
  roomSubscriptionListeners.add(listener);
  return () => {
    roomSubscriptionListeners.delete(listener);
  };
}

/**
 * connect-service reads `socket.handshake.auth` only. The legacy `emit('authentication')`
 * pattern runs after the handshake and is ignored there. Auth must be supplied here so
 * each connect / reconnect includes the current token from storage.
 */
const socket = io(config.eventRelayServer, {
  auth: (cb) => {
    resources.getToken().then((authToken) => {
      const token = authToken?.accessToken;
      cb(
        token !== undefined
          ? { token: token, facilityId: store.getState().common.facility?.id }
          : {},
      );
    });
  },
});

/** Rooms the app has asked to join; replayed on every `connect` (including reconnect). */
const activeServerRooms = new Set<string>();

let installedServerActionListener = false;

function installServerActionListener(): void {
  if (installedServerActionListener) {
    return;
  }
  installedServerActionListener = true;

  socket.on('action', (payload: ServerActionPayload) => {
    const type = typeof payload?.type === 'string' ? payload.type : '';
    const data = payload?.data as { room?: unknown } | undefined;
    const room = typeof data?.room === 'string' ? data.room : '';

    if (type === 'server/subscribed' && room !== '') {
      setRoomSubscriptionState(room, 'subscribed');
      return;
    }

    if (type === 'server/subscribeDenied' && room !== '') {
      setRoomSubscriptionState(room, 'denied');
    }
  });

  socket.on('disconnect', () => {
    for (const room of activeServerRooms) {
      setRoomSubscriptionState(room, 'unknown');
    }
  });
}

installServerActionListener();

socket.on('connect', () => {
  for (const room of activeServerRooms) {
    setRoomSubscriptionState(room, 'unknown');
    socket.emit('action', { type: 'server/subscribe', room });
  }
});

export function rememberServerRoomForResync(room: string): void {
  activeServerRooms.add(room);
  setRoomSubscriptionState(room, 'unknown');
}

export function forgetServerRoomForResync(room: string): void {
  activeServerRooms.delete(room);
  roomSubscriptionStateByRoom.delete(room);
}

/**
 * After login, `authService.storeLogin` updates localStorage; reconnect so the next
 * handshake sends the new `auth` payload (ignored if the page navigates away immediately).
 */
export function reconnectWithLatestAuth(): void {
  if (socket.connected) {
    socket.disconnect();
  }
  socket.connect();
}

export default socket;
